import * as activityHelpers from "@/helpers/ExhibitionActivity";
import * as localPlaceHelpers from "@/helpers/Place";
import { getDepartmentInfo } from "@/helpers/Group";
import aat from "@/data/aat.json";
import internal from "@/data/internal-ontologies.json";
import ulan from "@/data/ulan.json";
import { normalizeFieldToArray, getClassifiedAs } from "@thegetty/linkedart.js";
import {
  getEntityName,
  getDisplayDate,
  getDateRange
} from "@thegetty-private/linked-data.js";
import {
  getIndexedId,
  dateHelpers as appDateHelpers
} from "@thegetty-private/rad-app-utils";

const CARRIED_OUT_BY = "carried_out_by";
const CREATED_BY = "created_by";
const IDENTIFIED_BY = "identified_by";
const MOTIVATED = "motivated";
const MOTIVATED_BY = "motivated_by";
const TOOK_PLACE_AT = "took_place_at";
const EXACT_MATCH = "exact_match";
const EQUIVALENT = "equivalent";
export const CENTER = "CENTER";
export const VILLA = "VILLA";

/**
 * Returns a boolean indicating whether or not the supplied activity is a traveling exhibition
 *
 * @param {object} exhibition - a LOD exhibition Activity object
 *
 * @return {boolean} A boolean indicating whether or not the object is a traveling exhibition
 *
 */
export function isTravelingExhibition(exhibition) {
  return getClassifiedAs(exhibition, aat.TRAVELING_EXHIBITION).length;
}

/**
 * Returns an object from an array of exhibition activity objects. The
 * returned object has the keys "motivations" and "places", which correspond
 * to the type of stub.
 *
 * @param {array} exhibitions - an array of exhibition activity objects
 *
 * @return {object} An object with keys "motivations" and "places" with arrays
 * of Propositional Object and Place Object stubs as values respectively.
 *
 */
export function getAllStubs(exhibitions, baseUrl) {
  let motivationStubs = getStubSet(exhibitions, MOTIVATED_BY);
  let placeStubs = getStubSet(exhibitions, TOOK_PLACE_AT);
  // if passed a baseUrl add it to the stubs
  if (baseUrl) {
    placeStubs = placeStubs.map((stub) => ({ ...stub, baseUrl }));
    motivationStubs = motivationStubs.map((stub) => ({ ...stub, baseUrl }));
  }
  return { places: placeStubs, motivations: motivationStubs };
}

/**
 * Given an array of exhibition activity objects and a field, checks in the expected array of
 * each object and build an array of unique stubs found in all the objects.
 *
 * @param {array} exhibitions - an array of exhibition activity objects
 * @param {string} field - the top level field containing an array of objects
 *
 * @return {array} a unique array of stubs
 */
function getStubSet(exhibitions, field) {
  let stubSet = {};
  exhibitions.forEach((exhibition) => {
    let targetArray = normalizeFieldToArray(exhibition, field);
    let stubsObject = {};
    // add all stubs in the target array to an object with the form {stub.id: stub}
    targetArray.map((stub) => (stubsObject[stub.id] = stub));
    stubSet = { ...stubSet, ...stubsObject };
  });

  return Object.values(stubSet);
}

/**
 * Returns an object from an exhibition activity object representing the
 * exhibition in the object's motivated_by array with attributes "id",
 * "title", and "locations".
 *
 * @param {object} object - the exhibition activity object
 *
 * @return {object}
 *
 */
export function getTitleObject(object) {
  let returnObject = {};
  let motivatedBy = normalizeFieldToArray(object, MOTIVATED_BY);
  if (motivatedBy.length == 0) {
    motivatedBy = [object];
  }

  // get the motivation
  if (motivatedBy.length > 0) {
    returnObject.id = motivatedBy[0].id;
  }

  // get identifiedBy
  let identifiedBy = normalizeFieldToArray(motivatedBy[0], IDENTIFIED_BY);

  // filter to the event name
  let nameObj = getClassifiedAs(identifiedBy, aat.PREFERRED_TERMS);

  // set the title
  if (nameObj[0] && nameObj[0].content) {
    returnObject.title = nameObj[0].content;
  }

  // set locations to an empty array
  returnObject.locations = [];

  // return the matched object
  if (returnObject.title && returnObject.id) {
    return returnObject;
  }
}

/**
 * Takes a date range and returns a timespan string with the correct format:
 * [month1] [day1], [year1] to [month2] [day2], [year2] or
 * [month1] [day1] to [month2] [day2], [year] if within one year.
 *
 * @param {object} range - The range object from linkedArtHelpers.getRange with
 *  attributes gte and lte.
 *
 * @return {string} the formatted string
 *
 */
export function getTimespanString(range) {
  if (range == undefined) {
    return;
  }
  // look for timespan name in content first
  if ("content" in range) {
    return range.content;
  }
  // fall back to composing the timespan string from using gte and lte
  let date_string = getDisplayDate(range);

  // if we only have a single year for the range return that
  if (date_string.length == 4 && parseInt(date_string) != NaN) {
    return date_string;
  }

  // separate out the begin and end
  let [gte, lte] = date_string.split(" - ");
  let return_string = date_string.replace("-", "to");

  // if start and end are the same, break down the spans to months
  if (gte.slice(-4) == lte.slice(-4)) {
    let gte_day_month = gte.split(",")[0];
    return_string = gte_day_month + " to " + lte;
  }
  return return_string;
}

/**
 * Gets an object representing the location where an exhibition activity took place with
 * attributes "placeID", "locationName", and "timespan".
 *
 * @param {object} exhibition - the exhibition activity object
 * @param {array} places - the array of place objects passed into getExhibitionArray
 *
 * @return {object} the object representing the location of the exhibition
 *
 */
export function getLocationObject(exhibition, places = []) {
  let returnObject = {};
  // get the took place at
  let loc_array = normalizeFieldToArray(exhibition, TOOK_PLACE_AT);
  if (loc_array.length > 0) {
    returnObject.placeID = loc_array[0].id;
  }
  let indexedPlaceId = getIndexedId(returnObject.placeID);

  returnObject.locationName = undefined;

  // get the timespan (if there is one)
  if (exhibition.timespan) {
    returnObject.timespan = getDateRange(exhibition);
  }

  if (returnObject.placeID) {
    // look for the corresponding LOD Place object and use its name if available
    let fullPlace = places.find(
      (obj) => obj && indexedPlaceId == getIndexedId(obj.id)
    );

    if (fullPlace) {
      returnObject.locationName =
        localPlaceHelpers.getExhibitionNameWithPlace(fullPlace);
    }
    return returnObject;
  }
}

/**
 * Takes an object that contains Exhibitions, places, and motivations and returns the list of Index Exhibition IDs
 * associated with either a single stop exhibition, or the "parent" exhibition that represents the traveling exhibition (not the specific stops)
 *
 * @param {object} exhibitionPieces - the exhibition pieces
 *  - {array} exhibitions - the array of Exhibition Activities
 *  - {array} places - all of the places found in exhibition activity object
 *  - {array} motiviations - an array of exhibition propositional objects that represent the idea of the exhibition,
 *                           which may have occurred in several times and places
 *
 * @return {array} A list of indexedIds the top-level exhibitions that are displyed on the object page. This is a unique list of
 * the parent record for the traveling exhibitions or the stand-alone exhibtions
 */
export function getCanonicalExhibitionIds(exhibitionPieces) {
  // pass it to getExhibitionArray to resolve this to a set of exhibitions that we want to index
  let exhibitionArray = getExhibitionArray(exhibitionPieces);
  let indexedIds = [];
  exhibitionArray.forEach((exhibition) => {
    indexedIds.push(exhibition.id);
  });
  return indexedIds;
}

/**
 * Takes an object with an array of exhibition objects and returns an array of objects with raw data for
 * the indexer with attributes 'titleString', 'timespan', 'id', and 'locations'.
 *
 * @param {object} parts - the exhibition pieces
 *  - {array} exhibitions - the array of Exhibition Activities
 *  - {array} places - all of the places found in exhibition activity object
 *  - {array} motiviations - an array of exhibition propositional objects that represent the idea of the exhibition,
 *                           which may have occurred in several times and places
 * @param {object} logger - optional logger for emitting warnings
 *
 * @return {array} The array of objects with the exhibition's title string in 'title' and
 *  an array of the location strings in 'locations'.
 */
export function getExhibitionArray(parts, logger = undefined) {
  let exhibitions = parts.exhibitions || [];
  let places = parts.places || [];
  let motivations = parts.motivations || [];
  let exhibitionMap = {};
  // populate the exhibitionMap with the exhibition title objects:
  // {indexedId: {title: "title", id: indexedId, locations: []}}
  if (motivations && motivations.length > 0) {
    motivations.forEach((motivation) => {
      if (motivation && motivation.id) {
        exhibitionMap[getIndexedId(motivation.id)] = getTitleObject(motivation);
      }
    });
  }

  // add the information from the exhibition activities into the exhibitionMap:
  // {indexedId: {title: "title", id: indexedId, locations: [locationObject,...], timespan: timespanObject}}
  exhibitionMap = addExhibitionsToMap(
    exhibitions,
    places,
    exhibitionMap,
    logger
  );

  // update all ids for titleObjects and locationObjects to the indexedId format and add a "timespan"
  // attribute to titleObjects that don't already have one by consolidatig the timespans in the locations array
  let returnArray = exhibitionMapToArray(exhibitionMap);

  return returnArray;
}

/**
 * Adds the "locationObject" summaries of exhibition activity objects to the locations array
 * of each titleObject value in the exhibitionMap. Also handles any travelingExhibitions passed
 * in and uses them to add a timespan to the appropriate titleObject.
 *
 * @param {object} The exhibition activity object.
 * @param {array} The array of place objects passed into getExhibitionArray.
 * @param {object} The initial exhibitionMap {indexedId: titleObject}
 * @param {object} logger - optional logger for emitting warnings
 *
 * @return {object} The object representing the location of the exhibition.
 */
function addExhibitionsToMap(
  exhibitions,
  places,
  exhibitionMap,
  logger = undefined
) {
  for (let instance of exhibitions) {
    let canonicalExhibitID = getCanonicalID(instance, exhibitionMap, logger);
    // skip this exhibition activity if we cannot extract its
    if (!canonicalExhibitID) {
      continue;
    }
    // if the activity is a traveling exhibition, use it to get the timespan of the whole exhibit
    if (isTravelingExhibition(instance)) {
      exhibitionMap[canonicalExhibitID].timespan = getDateRange(instance);
    } else {
      // create a location object from the exhibition activity object
      let locationObject = getLocationObject(instance, places);
      // add the locationObject to the corresponding exhibition's locations array
      exhibitionMap[canonicalExhibitID].locations.push(locationObject);
    }
  }
  return exhibitionMap;
}

/**
 * Gets the canonicalExhibitID from an exhibition activity and checks that it is present in
 * the exhibitionMap. If it cannot find a canonicalExhibitID or the one it finds isn't in the
 * exhibitionMap it returns undefined and emits a warning.
 *
 * @param {object} exhibition - an LOD exhibition activity
 * @param {object} exhibitionMap - {indexedId: titleObject}
 * @param {object} logger - optional logger for emitting warnings
 *
 * @return {string} The ID of the PropositionalObject listed in the activity's "motivated_by" attribute.
 */
function getCanonicalID(exhibition, exhibitionMap, logger = undefined) {
  let canonicalExhibitID;
  if (exhibition?.motivated_by && exhibition.motivated_by.length > 0) {
    canonicalExhibitID = getIndexedId(exhibition.motivated_by[0].id);
  }

  // return undefined if we can't extract a
  if (!canonicalExhibitID || !exhibitionMap[canonicalExhibitID]) {
    if (logger && logger.warning) {
      logger.warning(
        `The Propositional Object with ID: ${canonicalExhibitID} was not passed in the exhibitions param`,
        exhibitionMap
      );
    }
    return;
  }
  return canonicalExhibitID;
}

/**
 * Changes the exhibitionMap into the returnArray format by updating all ids to indexedId format and
 * adding a timespan to any titleObject that doesn't already have one from a Traveling Exhibition.
 *
 * @param {object} exhibitionMap - {indexedId: titleObject}
 *
 * @return {array} the array of complete titleObjects
 */
function exhibitionMapToArray(exhibitionMap) {
  let exhibitionArray = Object.values(exhibitionMap);
  // remove titleObjects that an have empty locations array
  exhibitionArray = exhibitionArray.filter(
    (titleObject) => titleObject.locations.length > 0
  );
  for (let titleObject of exhibitionArray) {
    // change the ids from the full URLs to the indexedId format
    titleObject.id = getIndexedId(titleObject.id);
    titleObject.locations.forEach((location) => {
      location.placeID = getIndexedId(location.placeID);
    });
    if (titleObject.timespan == undefined) {
      titleObject.timespan = consolidateTimespans(titleObject.locations);
    }
  }
  return exhibitionArray;
}

/**
 * Takes an array of locationObjects and returns an object representing their combined timespan.
 *
 * @param {array} locationArray - an array of locationObjects {placeID, locationName, timespan}
 *  (from the titleObject "locations" attribute)
 *
 * @return {object} The timespan object representing the combined timespan of all the locationObjects
 */
function consolidateTimespans(locationArray) {
  let timespanArray = locationArray.map(
    (locationObject) => locationObject.timespan
  );

  // filter out any undefined values & return if that's all there was
  timespanArray = timespanArray.filter((timespan) => timespan != undefined);
  if (timespanArray.length == 0) {
    return;
  }

  let totalRange = appDateHelpers.getTotalRange(
    timespanArray,
    1,
    false,
    console
  );
  return {
    gte: totalRange.min.text,
    lte: totalRange.max.text
  };
}

/**
 * Takes an array of exhibition objects and returns an array of objects with the exhibition details
 * (name, locations, timespans) formatted for display on the HMO page exhibition tab
 *
 * @param {array} exhibitions - the array of exhibition objects
 *
 * @return {array} The array of objects with the exhibition and locations objects
 */
export function getExhibitionAndLocations(exhibitions) {
  return exhibitions.map((exhibition) => {
    // take each exhibition location and get the name and formatted timespan
    let locations = exhibition.locations.map((location) => {
      let locationData = {
        name: location.locationName
      };

      if (location.timespan) {
        locationData["timespan"] = getTimespanString(location.timespan);
      }

      return locationData;
    });

    return {
      id: exhibition.id,
      locations: locations,
      timespan: getTimespanString(exhibition.timespan),
      title: exhibition.title
    };
  });
}

// **EXHIBITION OBJECT HELPERS**

// for Exhibition Propositional Objects (for exhibition page)
/**
 * Takes an Exhibition object and gets the curator(s)
 *
 * @param {object} object - the JSON-LD of the Exhibition object
 *
 * @returns {array} an array of the Exhibition curator(s)
 */
export function getExhibitionCurators(object) {
  let allCurators = [];
  const createdBy = normalizeFieldToArray(object, CREATED_BY);

  if (!createdBy.length) {
    return allCurators;
  }

  createdBy.forEach((creatorObject) => {
    let carriedOutBy = normalizeFieldToArray(creatorObject, CARRIED_OUT_BY);
    let curators = getClassifiedAs(carriedOutBy, aat.CURATORS);
    allCurators.push(curators);
  });

  return allCurators.flat();
}

/**
 * Takes an Exhibition's venues and gets the combined timespan
 *
 * @param {array} venues - an array of an exhibition's venues (objects)
 *
 * @return {string} the combined timespan for the entire exhibition
 *
 */
export function getExhibitionTimespan(venues) {
  if (!venues) {
    return undefined;
  }

  let timespans = venues.map((venue) => {
    if (venue.timespan) {
      return getDateRange(venue);
    }
  });

  // filter out any undefined values & return if that's all there was
  timespans = timespans.filter((timespan) => timespan != undefined);
  if (timespans.length == 0) {
    return;
  }

  let totalRange = appDateHelpers.getTotalRange(timespans, 1, false, console);

  return getTimespanString({
    gte: totalRange.min.text,
    lte: totalRange.max.text
  });
}

/**
 * Takes an Exhibition object and gets the title
 *
 * @param {object} object - the JSON-LD of the Exhibition object
 *
 * @returns {string} the Exhibition title to display
 */
export function getExhibitionTitle(object) {
  return getEntityName(object);
}

/**
 * Takes an Exhibition object and gets the different activity/venue details
 *
 * @param {object} object - the JSON-LD of the Exhibition object
 *
 * @return {array} An array with all of the activity/venue full id(s) of an exhibition
 *
 */
export function getExhibitionActivityFullIds(object) {
  const motivated = normalizeFieldToArray(object, MOTIVATED);

  if (!motivated.length) {
    return [];
  }

  return motivated.map((actObj) => actObj.id);
}

// helpers for indexing Exhibition Propositional Objects

/**
 * Takes a list of ULAN IDs , returns which Getty location it is
 *
 * @param {array} ulanIds - the ulan ids for the place
 *
 * @return {string|undefined} - Getty location (if applicable)
 *
 */
export function getGettyLocation(ulanIDs) {
  if (isGettyCenterExhibition(ulanIDs)) {
    return CENTER;
  }

  if (isGettyVillaExhibition(ulanIDs)) {
    return VILLA;
  }

  return undefined;
}

/**
 * Takes a list of ULAN IDs, returns whether it is the Getty Center
 *
 * @param {array} ulanIds - the ulan ids for the place
 *
 * @return {boolean}
 *
 */
export function isGettyCenterExhibition(ulanIDs) {
  return (
    ulanIDs.filter((id) =>
      [ulan.GETTY_CENTER, ulan.GETTY_RESEARCH_INSTITUTE].includes(id)
    ).length > 0
  );
}

/**
 * Takes a list of ULAN IDs, returns whether it is the Getty Villa
 *
 * @param {array} ulanIds - the ulan ids for the place
 *
 * @return {boolean}
 *
 */
export function isGettyVillaExhibition(ulanIDs) {
  return (
    ulanIDs.filter((id) => [ulan.GETTY_VILLA, ulan.GETTY].includes(id)).length >
    0
  );
}

/**
 * Takes a list of ULAN IDs, returns whether it is a Getty exhibition
 *
 * @param {array} ulanIds - the ulan ids for the place
 *
 * @return {boolean}
 *
 */
export function isGettyExhibition(ulanIDs) {
  return isGettyCenterExhibition(ulanIDs) || isGettyVillaExhibition(ulanIDs);
}

/**
 * Takes all the activities/stops of an exhibition and returns an array of objects for each stop
 * with applicable data that has been formated for ES/to be indexed
 *
 * @param {object} data - an object of activities, places, and actors
 * @param {function} formatDatesForElasticsearch - indexer helper that formats dates for ES
 * @param {object} logger - logger functions for emitting logs/warnings
 *
 * @return {array} - an array of stops/activities objects that have been formatted for ES
 *
 */
export function getExhibitionStopsData(
  data = { activities, places, actors },
  formatDatesForElasticsearch,
  logger
) {
  let stops = [];
  if (data.places == undefined) {
    data.places = [];
  }
  if (data.actors == undefined) {
    data.actors = [];
  }
  data.activities.forEach((activity) => {
    let placeIndexedId = activityHelpers.getPlaceIndexedId(activity);
    let formattedDates;
    let timespanText;

    if (activity.timespan) {
      formattedDates = formatDatesForElasticsearch({
        gte: activity.timespan.begin_of_the_begin,
        lte: activity.timespan.end_of_the_end
      });

      // extra guard for known timespan error where start & end are swapped
      if (Date.parse(formattedDates.lte) < Date.parse(formattedDates.gte)) {
        logger.warn(
          "Flipping exhibition activity timespan dates for: " + placeIndexedId
        );
        let flip = formattedDates.gte;
        formattedDates.gte = formattedDates.lte;
        formattedDates.lte = flip;
      }

      timespanText = getTimespanString(formattedDates);
    }

    // need to use place-group equivalent
    let ulanIds = [];
    // get the place
    let locationName = undefined;
    normalizeFieldToArray(activity, TOOK_PLACE_AT).forEach((ref) => {
      let places = data.places.filter((place) => place.id == ref.id);

      // from the place, get the equivalent Actor
      places.forEach((place) => {
        let actors = data.actors.filter(
          (actor) =>
            getIndexedId(actor.id) ==
            activityHelpers.getEquivalentPlaceIndexedIds(place)[0]
        );

        // from the actor, get the ULAN ID
        actors.forEach((actor) => {
          normalizeFieldToArray(actor, EXACT_MATCH).forEach((match) => {
            ulanIds.push(match.id);
          });
          normalizeFieldToArray(actor, EQUIVALENT).forEach((match) => {
            ulanIds.push(match.id);
          });
        });
      });

      // if a location isn't a Getty Location (GRI 3rd party locations), then grab the location name
      let place = places.filter(
        (place) => getIndexedId(place.id) == placeIndexedId
      );
      if (place.length > 0) {
        locationName = getEntityName(place[0]);
      }
    });

    stops.push({
      placeId: placeIndexedId,
      locationName: locationName,
      isGettyExhibition: isGettyExhibition(ulanIds),
      gettyLocation: getGettyLocation(ulanIds),
      timespan: formattedDates,
      "timespan--text": timespanText
    });
  });
  return stops;
}

/**
 * Takes a list of all Getty Exhibition galleries to determine which
 * Getty location to display for an active exhibition
 *
 * @param {array} locations - an array of a Getty exhibition's galleries
 * @param {function} translate - the translate function
 *
 * @return {string} - which Getty location the exhibition is at
 *
 */
export function getExhibitionGettyLocation(locations, translate) {
  if (!locations) {
    return;
  }

  // campus (e.g. Getty Center or Getty Villa)
  let locationWords = locations[0].fullName.split(" ");
  let gettyLocation = `${locationWords[0]} ${locationWords[1].slice(0, -1)}`;

  let pavilionOrFloors = locations.map((location) => {
    if (gettyLocation.includes("Center") && location.pavillionName) {
      return `${location.pavillionName} ${translate(
        "exhibition_page.pavilion"
      )}`;
    }

    if (gettyLocation.includes("Villa") && location.floorName) {
      return location.floorName;
    }
  });

  // remove duplicates
  pavilionOrFloors = [...new Set(pavilionOrFloors)];

  return {
    campus: gettyLocation,
    pavilionOrFloors: pavilionOrFloors.join(", ")
  };
}

/**
 * Takes a list of exhibition places and returns the equivalent place / institutional id
 *
 * @param {array} places - an array of a Getty exhibition's places
 *
 * @return {array} - array of equivalent place ids
 *
 */
export function getExhibitionPlaceEquivalent(places) {
  if (!places || !Array.isArray(places)) {
    return;
  }
  let groups = [];
  places.forEach((place) => {
    groups.push(activityHelpers.getEquivalentPlaceIndexedIds(place)[0]);
  });

  return groups;
}

/**
 * Takes an exhibition LOD record and returns any associated curatorial departments
 *
 * @param {Object} exhibition - an LOD record for an exhibition
 * @returns {array} - an array of department objects for departments that carried out the exhibition
 */

export function getExhibitionDepartments(exhibition) {
  let allDepartments = [];
  const createdBy = normalizeFieldToArray(exhibition, CREATED_BY);
  if (!createdBy.length) {
    return allDepartments;
  }

  createdBy.forEach((creator) => {
    let carriedOutBy = normalizeFieldToArray(creator, CARRIED_OUT_BY);
    let departments = getClassifiedAs(carriedOutBy, aat.DEPARTMENTS);
    allDepartments.push(departments);
  });

  return allDepartments.flat();
}

/**
 * gets the Exhibition Brochure URL for the exhibition
 *
 * @param {object} exhibition - the JSON-LD
 *
 * @returns {string} the url
 */
export function getExhibitionBrochureUrl(exhibition) {
  let subjects = normalizeFieldToArray(exhibition, "subject_of");
  let urls = getClassifiedAs(subjects, internal.BROCHURE_URL);
  return urls[0]?.id;
}

/**
 * Tests whether the exhibition is a loan or not
 *
 * @param {array} activities - a list of JSON-LD Activities
 *
 * @returns {boolean} whether the exhibition is actually a loan
 */
export function isLongTermLoan(activities) {
  for (let activity of activities) {
    if (
      getClassifiedAs(activity, aat.LONG_TERM_LOAN).length > 0 ||
      getClassifiedAs(activity, internal.NON_EXHIBITION_LOAN).length > 0
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Tests whether the exhibition is a rotation or not
 *
 * @param {array} activities - a list of JSON-LD Activities
 *
 * @returns {boolean} whether the exhibition is a rotation
 */
export function isRotation(activities) {
  for (let activity of activities) {
    if (getClassifiedAs(activity, internal.EXHIBITION_ROTATION).length > 0) {
      return true;
    }
  }
  return false;
}

/**
 * Whether the exhibition is one of the GRI 3rd party exhibitions (pre-center founding)
 *
 * @param {object} exhibition - the exhbition JSON-LD object
 *
 * @returns {boolean}
 */
export function isGettyExternalExhibition(exhibition) {
  if (
    getClassifiedAs(exhibition, internal.GETTY_EXTERNAL_EXHIBITION).length > 0
  ) {
    return true;
  }
  return false;
}

/*
 *
 * @param {array} departments - a list of JSON-LD departments
 * @param {function} translate - the translate function
 * @returns {string} the formatted exhibition department contact string
 */

export function getExhibitionContact(departments, translate) {
  let departmentInfo = [];
  departments.forEach((department) => {
    const departmentDetails = getDepartmentInfo(department);
    departmentInfo.push(
      translate("exhibition_page.department_info", departmentDetails)
    );
  });
  let exhDepartmentStatement =
    departmentInfo.length <= 2
      ? departmentInfo.join(
          translate("exhibition_page.department_info_conjunction")
        )
      : departmentInfo
          .slice(0, departmentInfo.length - 1)
          .concat(
            translate("exhibition_page.department_info_conjunction") +
              departmentInfo.slice(-1)
          )
          .join(translate("exhibition_page.department_info_multi_conjunction"));
  return (
    translate("exhibition_page.department_info_prefix") + exhDepartmentStatement
  );
}
